import {
  Button,
  Collapse,
  Colors,
  Icon,
  IconSize,
  MenuItem,
  NonIdealState,
  Spinner,
} from "@blueprintjs/core";
import * as React from "react";
import { useInfiniteQuery } from "react-query";
import styled from "styled-components";

import { InboxRowDto, InboxSearchCriteria, InboxStep } from "../../api";
import { useCommonHooks } from "../../hooks";
import { ETLCodes } from "../../locales";
import InboxMenuItem from "./InboxMenuItem";

export const INBOX_PAGE_SIZE = 50;

export interface IInboxMenuDateCollapseProps {
  title: string;
  step: InboxStep;
  count?: number;
  defaultOpen?: boolean;
  onRowClick: (row: InboxRowDto) => void;
  archived?: boolean;
  userId: number;
  idpersonne?: number;
}

const StyledMenuItem = styled(MenuItem)`
  background-color: ${Colors.LIGHT_GRAY3};

  &:hover {
    background-color: ${Colors.LIGHT_GRAY1};
    & .collapse-icon {
      color: ${Colors.BLUE5} !important;
    }
  }
`;

const TextContainer = styled.div`
  display: flex;
  font-weight: bold;
  color: ${Colors.DARK_GRAY5};
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding-right: 0.5rem;
  & .collapse-icon {
    color: ${Colors.GRAY1} !important;
  }
`;

const InnerContainer = styled.div`
  padding: 2rem 0;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 1rem;
`;

export const InboxMenuDateCollapse: React.FunctionComponent<
  IInboxMenuDateCollapseProps
> = ({
  title,
  step,
  count,
  onRowClick,
  defaultOpen = false,
  archived = false,
  userId,
  idpersonne,
}) => {
  const {
    t,
    apis: { inbox },
  } = useCommonHooks();
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ["inbox-activity", userId, step, archived, idpersonne],
    async ({ pageParam = 0 }) =>
      await inbox.search(
        InboxSearchCriteria.fromJS({
          userId,
          archived,
          step,
          idpersonne,
          skip: pageParam,
          take: INBOX_PAGE_SIZE,
        })
      ),
    {
      enabled: isOpen,
      getNextPageParam: (lastPage, pages) =>
        lastPage.length < INBOX_PAGE_SIZE
          ? undefined
          : pages.reduce((total, page) => total + page.length, 0),
    }
  );

  const rows = React.useMemo(
    () => (data?.pages ?? []).flat(),
    [data?.pages]
  );

  const hasNoData = React.useMemo(
    () => !isFetching && isOpen && rows.length === 0,
    [isFetching, isOpen, rows.length]
  );

  // Le compteur évite la page vide finale quand le total est un multiple exact de la taille de page.
  const hasMore = React.useMemo(
    () => hasNextPage && (count === undefined || rows.length < count),
    [count, hasNextPage, rows.length]
  );

  return (
    <>
      <StyledMenuItem
        onClick={() => setIsOpen((prev) => !prev)}
        text={
          <TextContainer>
            <IconContainer>
              <Icon
                icon={isOpen ? "caret-down" : "caret-right"}
                className="collapse-icon"
                size={IconSize.LARGE}
              />
            </IconContainer>
            <span>
              {title}
              {count !== undefined && ` (${count})`}
            </span>
          </TextContainer>
        }
      />
      <Collapse isOpen={isOpen}>
        {isFetching && !isFetchingNextPage ? (
          <InnerContainer>
            <Spinner size={20} />
          </InnerContainer>
        ) : hasNoData ? (
          <InnerContainer>
            <NonIdealState description={t(ETLCodes.AucuneNotification)} />
          </InnerContainer>
        ) : (
          <>
            {rows.map((row, index) => (
              <InboxMenuItem
                key={index}
                row={row}
                onClick={onRowClick}
                interactive
                refetch={refetch}
                archived={archived}
              />
            ))}
            {hasMore && (
              <LoadMoreContainer>
                <Button
                  minimal
                  loading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  text={`${t(ETLCodes.ChargerPlus)}${
                    count !== undefined ? ` (${rows.length} / ${count})` : ""
                  }`}
                />
              </LoadMoreContainer>
            )}
          </>
        )}
      </Collapse>
    </>
  );
};

export default InboxMenuDateCollapse;
